import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '@/services/authApi';
import { AsTimeline, AsHero, AsDomainBadge, AsStatusBadge, AsStageEditor, AsStageSlider, type AsLogRow } from '../support/AsShared';
import { AS_FLOW, asLabel, parsePlan, activeStageKeys, type AsStageItem } from '../support/asMeta';
import s from '../support/As.module.scss';

interface AsRow { id: number; user_id: number; domain: string; product_name: string; symptom: string; status: string;
	contact_name: string | null; contact_phone: string | null; created_at: string; updated_at: string; completed_at: string | null; }
interface AsDetail { id: number; domain: string; product_name: string; product_model: string | null; symptom: string;
	company_name: string | null; address: string | null; purchase_source: string | null; install_date: string | null;   // [2026-09-12 폼개편] 회사명·주소(필수)+구매처·설치일(선택)
	contact_name: string | null; contact_phone: string | null; contact_email: string | null; status: string; stage_plan: string | null; plan_locked_at: string | null; created_at: string;
	visit_date: string | null; warranty: string | null; technician: string | null; technician_phone: string | null;
	completed_at: string | null; confirmed_at: string | null; assigned_admin_name?: string | null; }

const dt = (v: string | null) => (v ? String(v).slice(0, 16).replace('T', ' ') : '-');
const ymd = (v: string | null) => (v ? String(v).slice(0, 10) : '-');
// 상태 전이 선택지(스테퍼 8 + 예외 2). 관리자는 현장 유연성 위해 임의 전이 허용.
const ALL_STATUS = [...AS_FLOW, 'ON_HOLD', 'CANCELED'] as const;
// 필터 드롭다운은 도메인 혼합이라 generic 라벨(asLabel 인자 없음).
const STATUS_FILTERS = [{ v: '', t: '전체 상태' }, ...ALL_STATUS.map((st) => ({ v: st, t: asLabel(st) }))];
// [2026-09-12] 조회기간 빠른선택 — 오늘 기준 N개월 전 ~ 오늘. Autonics식 1/3/6개월.
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function monthsAgo(n: number): string { const d = new Date(); d.setMonth(d.getMonth() - n); return iso(d); }
const trunc = (s: string, n = 40) => (!s ? '-' : s.length <= n ? s : s.slice(0, n) + '…');

/**
 * [2026-09-12] 관리자 A/S 관리 — 도메인(안전/환경)·상태 필터 리스트 + 상세(상태전이·처리메모).
 *   상태변경/메모는 서버 as_status_log에 기록되어 고객 마이페이지 A/S현황과 유기적으로 공유.
 *   실무 흐름: 접수(고객) → 접수확인 → 원인분석 → 수리 → 완료통보 → 처리완료. 각 단계 처리내용을 메모로 남긴다.
 */
export default function AdminAs() {
	const [rows, setRows] = useState<AsRow[]>([]);
	const [total, setTotal] = useState(0);
	const [counts, setCounts] = useState<Record<string, number>>({});
	const [page, setPage] = useState(0);
	const [domain, setDomain] = useState('');
	const [status, setStatus] = useState('');
	const [keyword, setKeyword] = useState('');
	const [q, setQ] = useState('');
	const [from, setFrom] = useState('');   // 신청일 조회기간(YYYY-MM-DD)
	const [to, setTo] = useState('');
	const [period, setPeriod] = useState('');   // '' | '1' | '3' | '6' (개월) — 빠른선택 하이라이트
	const [loading, setLoading] = useState(false);
	const [sel, setSel] = useState<AsDetail | null>(null);
	const [logs, setLogs] = useState<AsLogRow[]>([]);
	const [nextStatus, setNextStatus] = useState('');
	const [note, setNote] = useState('');
	// [2026-09-12] ERP 메타 편집(방문예정일·보증·담당기사)
	const [mVisit, setMVisit] = useState('');
	const [mWarranty, setMWarranty] = useState('');
	const [mTech, setMTech] = useState('');
	const [mTechPhone, setMTechPhone] = useState('');   // 담당 기사 전화(관리자 내부용)
	// [2026-09-13] 진행 슬라이더 이동 제안(팝업) + 재구성(사유) 팝업
	const [move, setMove] = useState<{ to: string } | null>(null);
	const [moveNote, setMoveNote] = useState('');
	const [reconfigOpen, setReconfigOpen] = useState(false);
	const [reconfigReason, setReconfigReason] = useState('');
	const [busy, setBusy] = useState(false);
	const [msg, setMsg] = useState<string | null>(null);
	const size = 20;

	const load = useCallback(async () => {
		setLoading(true);
		const r = await adminApi.asList(q, domain, status, from, to, page, size);
		setLoading(false);
		if (r.success) {
			setRows((r.items as AsRow[]) || []); setTotal(Number(r.total) || 0);
			const c: Record<string, number> = {};
			((r.counts as { status: string; cnt: number }[]) || []).forEach((x) => { c[x.status] = Number(x.cnt); });
			setCounts(c);
		}
	}, [q, domain, status, from, to, page]);
	useEffect(() => { load(); }, [load]);

	// 조회기간 빠른선택(1/3/6개월) — from/to 세팅. '전체'는 해제.
	function pickPeriod(m: string) {
		setPeriod(m); setPage(0);
		if (!m) { setFrom(''); setTo(''); return; }
		setFrom(monthsAgo(Number(m))); setTo(iso(new Date()));
	}

	async function open(id: number) {
		setMsg(null); setNote(''); setNextStatus('');
		const r = await adminApi.asDetail(id);
		if (r.success) {
			const req = r.request as AsDetail;
			setSel(req); setLogs((r.timeline as AsLogRow[]) || []); setNextStatus(req.status);
			setMVisit(req.visit_date ? String(req.visit_date).slice(0, 10) : ''); setMWarranty(req.warranty || ''); setMTech(req.technician || ''); setMTechPhone(req.technician_phone || '');
		}
	}
	async function saveMeta() {
		if (!sel) return;
		setBusy(true);
		const r = await adminApi.asSetMeta(sel.id, mVisit, mWarranty, mTech, mTechPhone);
		setBusy(false);
		if (r.success) refresh(sel.id); else setMsg(r.message || '저장 실패');
	}
	async function refresh(id: number) {
		const r = await adminApi.asDetail(id);
		if (r.success) { setSel(r.request as AsDetail); setLogs((r.timeline as AsLogRow[]) || []); setNextStatus((r.request as AsDetail).status); }
	}
	async function applyStatus() {
		if (!sel || !nextStatus) return;
		setBusy(true);
		const r = await adminApi.asSetStatus(sel.id, nextStatus, note.trim());
		setBusy(false);
		if (r.success) { setNote(''); refresh(sel.id); load(); } else setMsg(r.message || '실패했습니다.');
	}
	async function sendMsg() {
		if (!sel || !note.trim()) return;
		const r = await adminApi.asMessage(sel.id, note.trim());
		if (r.success) { setNote(''); refresh(sel.id); } else setMsg(r.message || '실패했습니다.');
	}
	// [2026-09-13] 케이스별 단계 구성(순서·활성) 저장 → DB(/plan). 확정(잠금) 후엔 서버가 거부.
	async function savePlan(next: AsStageItem[]) {
		if (!sel) return;
		const r = await adminApi.asSetPlan(sel.id, next.map((p) => ({ key: p.key, active: p.active })));
		if (r.success) refresh(sel.id); else setMsg(r.message || '단계 구성 저장에 실패했습니다.');
	}
	// [2026-09-13] 진행 슬라이더: 이동 제안(팝업) → 내용/사유 입력 후 [적용]해야 실제 전이(DB). 뒤로=되돌림=사유 필수.
	function propose(to: string) { setMove({ to }); setMoveNote(''); setMsg(null); }
	async function applyMove() {
		if (!sel || !move) return;
		const backward = AS_FLOW.indexOf(move.to) < AS_FLOW.indexOf(sel.status);
		if (backward && !moveNote.trim()) { setMsg('되돌림에는 사유를 입력해야 합니다.'); return; }
		setBusy(true);
		const r = await adminApi.asSetStatus(sel.id, move.to, moveNote.trim());
		setBusy(false);
		if (r.success) { setMove(null); setMoveNote(''); refresh(sel.id); load(); } else setMsg(r.message || '실패했습니다.');
	}
	// 구성 확정(잠금)·진행 시작 / 재구성(사유 필수, 잠금 해제).
	async function doCommit() {
		if (!sel) return;
		const r = await adminApi.asCommit(sel.id);
		if (r.success) refresh(sel.id); else setMsg(r.message || '확정에 실패했습니다.');
	}
	async function doReconfigure() {
		if (!sel || !reconfigReason.trim()) return;
		const r = await adminApi.asReconfigure(sel.id, reconfigReason.trim());
		if (r.success) { setReconfigOpen(false); setReconfigReason(''); refresh(sel.id); } else setMsg(r.message || '재구성에 실패했습니다.');
	}
	function search(e: React.FormEvent) { e.preventDefault(); setPage(0); setQ(keyword.trim()); }

	const totalPages = Math.max(1, Math.ceil(total / size));
	const openCount = (counts.RECEIVED || 0) + (counts.CONFIRMED || 0) + (counts.DIAGNOSIS || 0) + (counts.REPAIR || 0) + (counts.NOTIFIED || 0);
	// [2026-09-13] 선택 케이스의 단계 구성(순서+활성) · 활성 단계 키 · 구성 확정(잠금) 여부.
	const selPlan = sel ? parsePlan(sel.stage_plan) : [];
	const selSteps = activeStageKeys(selPlan);
	const planLocked = !!sel?.plan_locked_at;   // 확정됨 → 진행(슬라이더) / 미확정 → 설정(편집기)

	return (
		<>
			<p className={s.eyebrow}>A/S MANAGEMENT</p>
			<h2 className={s.h2}>A/S <b>관리</b></h2>
			<p className={s.lead}>고객 A/S 접수를 확인하고 상태를 전이하며 처리내용을 남깁니다. 변경사항은 고객 화면에 실시간 공유됩니다.</p>
			{/* [2026-09-12 사용자] 안전/환경 단계 차이 안내 — 고객 화면과 동일 공용 컴포넌트. */}
			{null}

			<div className={s.asKpiGrid}>
				<div className={s.asKpi}>
					<div className={`${s.asKpiIco} ${s.asKpiA}`}><svg viewBox="0 0 24 24" fill="none" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M20 11a8 8 0 1 0-2.3 5.7M20 6v5h-5" /></svg></div>
					<div className={s.asKpiVal}>{openCount}</div><div className={s.asKpiLabel}>진행중</div>
				</div>
				<div className={s.asKpi}>
					<div className={`${s.asKpiIco} ${s.asKpiG}`}><svg viewBox="0 0 24 24" fill="none" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM8.5 12l2.5 2.5 4.5-5" /></svg></div>
					<div className={s.asKpiVal}>{counts.COMPLETED || 0}</div><div className={s.asKpiLabel}>완료</div>
				</div>
				<div className={s.asKpi}>
					<div className={`${s.asKpiIco} ${s.asKpiW}`}><svg viewBox="0 0 24 24" fill="none" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM10 9v6M14 9v6" /></svg></div>
					<div className={s.asKpiVal}>{counts.ON_HOLD || 0}</div><div className={s.asKpiLabel}>보류</div>
				</div>
				<div className={s.asKpi}>
					<div className={`${s.asKpiIco} ${s.asKpiC}`}><svg viewBox="0 0 24 24" fill="none" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM9 9l6 6M15 9l-6 6" /></svg></div>
					<div className={s.asKpiVal}>{counts.CANCELED || 0}</div><div className={s.asKpiLabel}>취소</div>
				</div>
			</div>

			{/* 조회기간(신청일) — 빠른선택 + 일자조회 */}
			<div className={s.filterBar}>
				<span className={s.filterLabel}>조회기간</span>
				{[{ v: '', t: '전체' }, { v: '1', t: '1개월' }, { v: '3', t: '3개월' }, { v: '6', t: '6개월' }].map((p) => (
					<button key={p.v} type="button" className={`${s.chip} ${period === p.v ? s.chipOn : ''}`} onClick={() => pickPeriod(p.v)}>{p.t}</button>
				))}
				<input className={s.dateInput} type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPeriod(''); setPage(0); }} />
				<span style={{ color: '#6b7480' }}>~</span>
				<input className={s.dateInput} type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPeriod(''); setPage(0); }} />
			</div>

			<form onSubmit={search} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
				<select className={s.select} style={{ width: 'auto' }} value={domain} onChange={(e) => { setDomain(e.target.value); setPage(0); }}>
					<option value="">전체 분야</option><option value="SAFETY">안전</option><option value="ENV">환경</option>
				</select>
				<select className={s.select} style={{ width: 'auto' }} value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
					{STATUS_FILTERS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
				</select>
				<input className={s.input} style={{ flex: 1, minWidth: 180 }} value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="제품·증상·신청자·전화 검색" />
				<button className={s.ghost} type="submit">검색</button>
			</form>

			<div className={s.tableWrap}>
				<table className={s.table}>
					<thead><tr>
						<th>접수번호</th><th>분야</th><th>A/S 제품</th><th>증상·문제점</th><th>신청일</th><th>현황</th><th>완료일</th><th aria-hidden></th>
					</tr></thead>
					<tbody>
						{rows.length === 0 ? <tr><td colSpan={8} className={s.empty}>{loading ? '불러오는 중…' : 'A/S 내역이 없습니다.'}</td></tr>
							: rows.map((r) => (
								<tr key={r.id} onClick={() => open(r.id)}>
									<td className={s.mono}>AS-{String(r.id).padStart(6, '0')}</td>
									<td><AsDomainBadge domain={r.domain} /></td>
									{/* 줄바꿈 방지는 .table td { white-space: nowrap }(As.module.scss)에서 공통 처리 — 조용한 짤림 컨벤션. */}
									<td style={{ fontWeight: 500 }}>{r.product_name}</td>
									<td style={{ color: '#5b6b82' }}>{trunc(r.symptom)}</td>
									<td className={s.mono}>{ymd(r.created_at)}</td>
									<td><AsStatusBadge status={r.status} domain={r.domain} /></td>
									<td className={s.mono}>{ymd(r.completed_at)}</td>
									<td style={{ textAlign: 'right' }}><span className={s.rowGo}>›</span></td>
								</tr>
							))}
					</tbody>
				</table>
			</div>

			<div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', marginTop: 20 }}>
				<button className={s.ghost} disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>이전</button>
				<span style={{ fontSize: 13, color: '#6b7480' }}>{page + 1} / {totalPages} · 총 {total}건</span>
				<button className={s.ghost} disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>다음</button>
			</div>

			{sel && (
				<div className={s.modalBack} data-lenis-prevent onClick={() => setSel(null)}>
					<div className={s.modal} onClick={(e) => e.stopPropagation()}>
						<div className={s.modalHead}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								<AsDomainBadge domain={sel.domain} />
								<span className={s.asNo}>AS-{String(sel.id).padStart(6, '0')}</span>
							</div>
							<button className={s.mClose} onClick={() => setSel(null)} aria-label="닫기">✕</button>
						</div>
						<div className={s.modalBody}>
							{msg && <div className={`${s.msg} ${s.msgErr}`}>{msg}</div>}
							<AsHero status={sel.status} domain={sel.domain} steps={selSteps} product={sel.product_name} createdAt={sel.created_at} locked={planLocked} />

							{/* [2026-09-13 사용자 확정] ★큰 조작 스테퍼(히어로 바로 아래) — 확정 전=구성 편집(접수 고정 + 이후 활성/순서)+[확정] / 확정 후=진행 슬라이더+[재구성]. 읽기전용 스테퍼는 고객 화면에만. */}
							<div style={{ margin: '4px 0 28px' }}>
								<div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14, flexWrap: 'wrap' }}>
									<span style={{ fontSize: 16.5, fontWeight: 800, color: '#0E86BC' }}>{planLocked ? '진행 · 단계 이동' : '단계 구성 (설정)'}</span>
									{planLocked
										? <span style={{ fontSize: 11.5, fontWeight: 600, color: '#0f6e4e', background: '#e9f7f0', borderRadius: 20, padding: '2px 10px' }}>구성 확정됨 🔒</span>
										: <span style={{ fontSize: 11.5, fontWeight: 600, color: '#b0870f', background: '#fdf6e6', borderRadius: 20, padding: '2px 10px' }}>미확정 · 설정 중</span>}
								</div>
								{planLocked ? (
									<>
										<AsStageSlider steps={selSteps} status={sel.status} onPropose={propose} />
										<div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
											{/* [2026-09-13 사용자] 확정 버튼은 사라지지 않고 disable(회색)로 남겨 상태를 시각화 — UX 이해 도움. */}
											<button className={s.primary} style={{ width: 'auto', padding: '11px 24px', opacity: .5, cursor: 'default' }} disabled>✓ 구성 확정됨 · 진행 중</button>
											<button className={s.ghost} onClick={() => { setReconfigReason(''); setReconfigOpen(true); }}>구성 재구성</button>
											<span style={{ fontSize: 11.5, color: '#8894a0' }}>확정 후 구성 편집·순서변경은 <b style={{ color: '#6b7480' }}>금지</b> — 바꾸려면 재구성(사유 기록·이력 보존)</span>
										</div>
									</>
								) : (
									<>
										<AsStageEditor plan={selPlan} onChange={savePlan} />
										{/* [2026-09-13 사용자] 확정 전 진행 슬라이더는 DISABLE로 미리보기(진행은 확정 후) */}
										<div className={s.slRail} style={{ opacity: .4, pointerEvents: 'none', marginTop: 10 }}>
											<div className={s.slTrack} /><div className={s.slThumb} style={{ left: '6%' }} />
										</div>
										<div style={{ fontSize: 11.5, color: '#8894a0', margin: '6px 0 0' }}>↑ 진행 슬라이더는 <b style={{ color: '#6b7480' }}>[구성 확정]</b> 후 활성화됩니다.</div>
										<div style={{ marginTop: 16 }}>
											<button className={s.primary} style={{ width: 'auto', padding: '11px 24px' }} onClick={doCommit} disabled={busy}>구성 확정 · 진행 시작</button>
											<div style={{ fontSize: 11.5, color: '#8894a0', marginTop: 8 }}>확정 = 접수 확인·승인 → <b style={{ color: '#6b7480' }}>접수 자동 완료</b> 후 다음 단계부터 진행. 구성은 잠깁니다(편집 금지).</div>
										</div>
									</>
								)}
							</div>

							{sel.status === 'COMPLETED' && (
								<div style={{ margin: '20px 0 0', fontSize: 13.5, color: sel.confirmed_at ? '#0f6e4e' : '#b0870f', background: sel.confirmed_at ? '#e9f7f0' : '#fdf6e6', border: `1px solid ${sel.confirmed_at ? '#bfe6d3' : '#f3e4bf'}`, borderRadius: 10, padding: '11px 15px', lineHeight: 1.5 }}>
									{sel.confirmed_at ? `✓ 고객이 수령·완료를 확인했습니다 · ${dt(sel.confirmed_at)}` : '고객 수령·완료 확인 대기 중 (고객이 마이페이지에서 확인).'}
								</div>
							)}

							<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', margin: '22px 0 14px' }}>
								<Info k="제품" v={sel.product_name} />
								<Info k="모델명" v={sel.product_model || '-'} />
								<Info k="회사명" v={sel.company_name || '-'} />
								<Info k="설치일" v={ymd(sel.install_date)} />
								<Info k="신청자" v={sel.contact_name || '-'} />
								<Info k="연락처" v={sel.contact_phone || '-'} />
								<Info k="구매처" v={sel.purchase_source || '-'} />
								<Info k="현재 담당 승인자" v={sel.assigned_admin_name || '미배정'} />
							</div>
							{/* [2026-09-12 폼개편] 주소=길 수 있어 그리드 밖 전체폭 한 줄. 방문/입고 처리에 쓰는 현장 주소. */}
							<div style={{ fontSize: 13.5, color: '#4a5361', margin: '0 0 14px' }}>
								<span style={{ color: '#8894a0' }}>주소</span> &nbsp; {sel.address || '-'}
							</div>
							<div style={{ fontSize: 14.5, color: '#4a5361', background: '#f6f8fb', borderRadius: 10, padding: '14px 16px', marginBottom: 20, lineHeight: 1.65 }}>
								<b style={{ color: '#1a2230' }}>증상</b> · {sel.symptom}
							</div>

							{/* [2026-09-12] 현장·보증 정보(방문/입고 예정일·무상/유상·담당기사) */}
							<div style={{ border: '1px solid #e5e8ec', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
								<div style={{ fontSize: 15, fontWeight: 700, color: '#1a2230', marginBottom: 10 }}>
									처리 · 보증 정보
								</div>
								<div style={{ fontSize: 12.5, color: '#8894a0', margin: '0 0 12px', lineHeight: 1.55 }}>현장 출동 또는 제품 입고로 처리합니다. 처리 예정일 · 보증(무상=보증기간 내 / 유상=기간 외) · 담당 기사를 지정합니다. (현장/입고 여부는 단계 구성·메모로 관리)</div>
									<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 10 }}>
									<label style={{ fontSize: 12, color: '#6b7480' }}>처리 예정일
										<input className={s.dateInput} type="date" style={{ width: '100%', marginTop: 4 }} value={mVisit} onChange={(e) => setMVisit(e.target.value)} /></label>
									<label style={{ fontSize: 12, color: '#6b7480' }}>보증
										<select className={s.select} style={{ marginTop: 4 }} value={mWarranty} onChange={(e) => setMWarranty(e.target.value)}>
											<option value="">미정</option><option value="FREE">무상</option><option value="PAID">유상</option>
										</select></label>
									<label style={{ fontSize: 12, color: '#6b7480' }}>담당 기사
										<input className={s.input} style={{ marginTop: 4 }} value={mTech} onChange={(e) => setMTech(e.target.value)} placeholder="담당자명" maxLength={60} /></label>
										<label style={{ fontSize: 12, color: '#6b7480' }}>담당 기사 전화 <span style={{ color: '#c58a1a', fontWeight: 600 }}>· 내부용</span>
											<input className={s.input} style={{ marginTop: 4 }} value={mTechPhone} onChange={(e) => setMTechPhone(e.target.value)} placeholder="'-' 없이" inputMode="numeric" maxLength={30} /></label>
								</div>
								<div style={{ fontSize: 11.5, color: '#b08636', background: '#fdf6e6', border: '1px solid #f3e4bf', borderRadius: 8, padding: '7px 11px', margin: '0 0 10px', lineHeight: 1.5 }}>담당 기사 <b>전화</b>는 관리자 내부용 — 고객 화면·타임라인 미노출. 고객에겐 대표 A/S 번호로 안내됩니다.</div>
									<div style={{ fontSize: 12, color: '#6b7480', margin: '0 0 10px', lineHeight: 1.5 }}>입력 후 <b style={{ color: '#6b7480' }}>이 버튼</b>을 눌러야 반영됩니다 (다른 버튼·닫기로는 저장 안 됨).</div>
									<button className={s.ghost} onClick={saveMeta} disabled={busy}>현장·보증 정보 저장</button>
							</div>

							{/* 상태 변경 없이 고객 안내만 — 언제나 가능(진행 이력 위). */}
							<div style={{ marginBottom: 20 }}>
								<textarea className={s.textarea} style={{ minHeight: 58 }} value={note} onChange={(e) => setNote(e.target.value)}
									placeholder="상태 변경 없이 고객에게 안내/메모만 남기기 (선택)" maxLength={2000} />
								<div style={{ marginTop: 8 }}><button className={s.ghost} onClick={sendMsg} disabled={!note.trim()}>메모만 남기기</button></div>
							</div>

							{/* [2026-09-13] 상태 변경은 위 진행 슬라이더(팝업+적용)로 통합됨 — 기존 드롭다운 패널 제거. */}

							<div style={{ fontSize: 15, fontWeight: 700, color: '#1a2230', margin: '4px 0 4px' }}>진행 이력</div>
							<div style={{ fontSize: 12, color: '#6b7480', margin: '0 0 12px', lineHeight: 1.5 }}>고객·관리자가 남긴 기록이 시간순으로 쌓입니다 (양쪽 화면 공유).</div>
							<AsTimeline rows={logs} domain={sel.domain} />

							{/* [2026-09-13 사용자 확정] 진행 슬라이더 이동 팝업 — 내용/사유 입력 후 [적용]해야 실제 전이(DB). 뒤로=되돌림=사유 필수. */}
							{move && (() => {
								const backward = AS_FLOW.indexOf(move.to) < AS_FLOW.indexOf(sel.status);
								return (
									<div className={s.modalBack} style={{ zIndex: 60 }} data-lenis-prevent onClick={() => { setMove(null); setMsg(null); }}>
										<div className={s.modal} style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
											<div className={s.modalBody} style={{ padding: '22px 24px' }}>
												<div style={{ fontSize: 17, fontWeight: 800, color: backward ? '#c58a1a' : '#0E86BC', marginBottom: 6 }}>{backward ? '되돌림' : '단계 이동'}</div>
												<div style={{ fontSize: 14, color: '#4a5361', marginBottom: 14, lineHeight: 1.6 }}>
													<b>{asLabel(sel.status)}</b> → <b style={{ color: backward ? '#c58a1a' : '#16A6E4' }}>{asLabel(move.to)}</b>{backward ? ' (뒤로 되돌립니다)' : ' 로 진행합니다'}
												</div>
												<label style={{ fontSize: 12.5, fontWeight: 600, color: '#1a2230', display: 'block', marginBottom: 6 }}>
													{backward ? '되돌림 사유' : `‘${asLabel(sel.status)}’ 처리 내용`}
													<span style={{ color: backward ? '#c0392b' : '#98a1ac', fontWeight: 500, marginLeft: 4 }}>{backward ? '(필수)' : '(권장)'}</span>
												</label>
												<textarea className={s.textarea} style={{ minHeight: 72 }} value={moveNote} onChange={(e) => setMoveNote(e.target.value)}
													placeholder={backward ? '왜 이전 단계로 되돌리는지 사유 (예: 배송 후 재고장, 재입고)' : '이 단계에서 확인·처리한 내용 (예: DC 2선 통신 불량 확인)'} maxLength={2000} />
												{msg && <div className={`${s.msg} ${s.msgErr}`} style={{ marginTop: 10 }}>{msg}</div>}
												<div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
													<button className={s.primary} style={{ width: 'auto', padding: '11px 22px' }} disabled={busy || (backward && !moveNote.trim())} onClick={applyMove}>{busy ? '적용 중…' : '적용'}</button>
													<button className={s.ghost} onClick={() => { setMove(null); setMsg(null); }}>취소</button>
												</div>
											</div>
										</div>
									</div>
								);
							})()}

							{/* [2026-09-13 사용자 확정] 구성 재구성 팝업 — 사유 필수. 확정 잠금 해제, 과거 이력 보존. */}
							{reconfigOpen && (
								<div className={s.modalBack} style={{ zIndex: 60 }} data-lenis-prevent onClick={() => setReconfigOpen(false)}>
									<div className={s.modal} style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
										<div className={s.modalBody} style={{ padding: '22px 24px' }}>
											<div style={{ fontSize: 17, fontWeight: 800, color: '#0E86BC', marginBottom: 6 }}>단계 구성 재구성</div>
											<div style={{ fontSize: 13.5, color: '#4a5361', marginBottom: 14, lineHeight: 1.6 }}>확정된 구성을 다시 편집합니다. 과거 진행 이력은 보존되며, 재구성 사실이 <b>작성자·시각·사유</b>와 함께 기록됩니다.</div>
											<label style={{ fontSize: 12.5, fontWeight: 600, color: '#1a2230', display: 'block', marginBottom: 6 }}>재구성 사유 <span style={{ color: '#c0392b', fontWeight: 500 }}>(필수)</span></label>
											<textarea className={s.textarea} style={{ minHeight: 72 }} value={reconfigReason} onChange={(e) => setReconfigReason(e.target.value)}
												placeholder="왜 구성을 바꾸는지 사유 (예: 현장 처리 불가 → 입고 수리로 전환)" maxLength={2000} />
											<div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
												<button className={s.primary} style={{ width: 'auto', padding: '11px 22px' }} disabled={busy || !reconfigReason.trim()} onClick={doReconfigure}>재구성</button>
												<button className={s.ghost} onClick={() => setReconfigOpen(false)}>취소</button>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
}

function Info({ k, v }: { k: string; v: string }) {
	return (
		<div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eef0f3', padding: '9px 0' }}>
			<span style={{ color: '#6b7480', fontSize: 14 }}>{k}</span>
			<span style={{ fontSize: 14.5, color: '#1a2230', fontWeight: 500, textAlign: 'right' }}>{v}</span>
		</div>
	);
}
